import { v4 as uuidv4 } from 'uuid';
import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex, FormControl, useToast } from '@withjoy/joykit';
import { Drag, Error, Info, Pencil, WarningTriangle } from '@withjoy/joykit/icons';
import { AiSparkleIcon } from '../MoreMenu/AiSparkleIcon';
import { CheckIcon } from '@assets/icons';
import { addError } from '@shared/utils/logger';
import { useHistory, useRouterHelper, useTranslation } from '@shared/core';
import { useAccommodationsContext } from '../../context/useAccommodationsContext';
import { useNavigationPaneContext } from '@website-editor/context/NavigationPaneContext/NavigationPane.context';
import { useSortedAccommodationsData } from '../../hooks/useSortedAccommodationsData';
import { useAdminAccommodationsTelemetry } from '../../Accommodations.telemetry';
import { useRemoveHotel } from '../../hooks/useRemoveHotel';
import { useManageMode } from '../../hooks/useManageMode';
import { useBulkRemoveHotels } from '../../hooks/useBulkRemoveHotels';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useUpdateSearchParam } from '@shared/utils/updateSearchParam';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useGetStartedTarget, useGetStartedTour } from '@shared/components/GetStartedOnboarding';
import type { GetStartedTargetRegistrationOptions } from '@shared/components/GetStartedOnboarding';
import { useTravelPlacesPricingAndBookingDetailsQuery } from '@shared/hooks/useTravelPlacesPricingAndBookingDetails';
import { AutopopulateAccommodationPlacesScenario, UserFeedbackDtoEnum } from '@graphql/generated-base';
import { ACCOMMODATIONS_ACTIONS } from '../../Accommodations.constants';
import { ADD_STAY_SEARCH_PARAM_KEY } from '@shared/utils/accommodations';
import { PlanningRoutes } from '@apps/admin/routes/Planning/Planning.routes';
import { TravelMapBlockType } from '../../Accommodations.types';
import { AsyncAutoPopulationStatus } from '../AsyncAutoPopulationStatus';
import { LazyAiRecommendationsFeedback } from '../AiRecommendationsFeedback/AiRecommendationsFeedback.lazy';
import { HotelRemoveDialog } from '../HotelRemoveDialog/HotelRemoveDialog';
import { HotelSuggestDialog } from '../HotelSuggestDialog/HotelSuggestDialog';
import { BulkRemoveDialog } from '../BulkRemoveDialog/BulkRemoveDialog';
import { DiscardReorderDialog } from '../DiscardReorderDialog/DiscardReorderDialog';
import TabSectionHeader from '../TabSectionHeader/TabSectionHeader';
import { StartTourButton } from '../AccommodationsTour';
import { useAutoPopulatedAccommodations } from '../../hooks/useAutoPopulatedAccommodations';
import { HotelsList } from './HotelsList';
import HotelsEmptyState from './HotelsEmptyState';
import { getEmptyStateSuggestAction } from './HotelsTab.utils';
import { useUpdatePageContainerMutation } from '../../../../features/custom-page/WebsiteEditorCustomPage.graphql.generated';
import { useUpdateTravelMapComponentMutation } from '../../Accommodations.graphql.generated';
import { useUpdateWebsitePreview } from '@website-editor/hooks/useUpdateWebsitePreview';
import { useRemoteFeatureFlag } from '@shared/core/featureFlags/remoteFeatureFlags';
import { LazyMoreMenu } from '../MoreMenu/MoreMenu.lazy';
import type { MoreMenuItem } from '../MoreMenu/MoreMenu.types';
import { useAutoPopulation } from '@shared/utils/accommodations';

import { PageNoteField, PageNoteLabel } from './components/PageNoteField';
import { HotelTileSkeletonList } from '@shared/components/HotelTile';
import { sendMessageToPreview } from '@apps/admin/common/InlineEditManager/utils/SendMessageToPreview.utils';
import { withWindow } from '@shared/utils/withWindow';
import type { TravelCurrencyCode } from '@graphql/generated-base';
import type { AccommodationCard } from '../../Accommodations.types';
import type { SuggestionOption, SuggestionOptionId } from '../HotelSuggestDialog/HotelSuggestDialog.types';

const EDIT_HOTEL_ID_KEY = 'addMatchedHotel';
const HOTEL_SUGGEST_SHOWN_PREFIX = 'joy:admin:hotelSuggestDialogShown';

type HotelSuggestionOptionId = Exclude<SuggestionOptionId, 'suggestNeighborhoods'>;

const USER_FEEDBACK_BY_OPTION: Record<HotelSuggestionOptionId, UserFeedbackDtoEnum> = {
  mixBudgets: UserFeedbackDtoEnum.SIMILAR,
  mostlyAffordable: UserFeedbackDtoEnum.MORE_AFFORDABLE,
  mostlyUpscale: UserFeedbackDtoEnum.LUXURY,
  whateverClosest: UserFeedbackDtoEnum.CLOSER,
};

type RequestAccommodationPlacePricingProps = Readonly<{
  accommodationPlaceIds: string[];
  eventCurrency?: TravelCurrencyCode | null;
}>;

const RequestAccommodationPlacePricing = ({ eventCurrency, accommodationPlaceIds }: RequestAccommodationPlacePricingProps): null => {
  useTravelPlacesPricingAndBookingDetailsQuery(accommodationPlaceIds, 'accommodationPlace', false, {}, false, eventCurrency);
  return null;
};

/**
 * Narrows the "Get started" tour anchor from the ref-holding wrapper to the Add button itself —
 * the wrapper picks up a few extra pixels from the header's flex layout, which would push the
 * coachmark and its awaiting ring off the button's edge.
 */
const GET_STARTED_STAY_TARGET_OPTIONS: GetStartedTargetRegistrationOptions = {
  resolveAnchor: wrapper => wrapper.querySelector('button') ?? wrapper,
};

const HotelsTab: React.FC = () => {
  const { accommodationsData, componentData, pageNote, pageContainerId, refetchPageContainer, venueData, eventHandle, pageSlug, isSuperAdmin } = useAccommodationsContext();
  const { navigateToScreen } = useNavigationPaneContext();
  const telemetry = useAdminAccommodationsTelemetry();
  // "Get started" tour, step 4: the Add button (either header variant) anchors the coachmark/ring.
  const { completeTask: completeGetStartedTask } = useGetStartedTour();
  const getStartedStayTargetRef = useGetStartedTarget('stay', GET_STARTED_STAY_TARGET_OPTIONS);
  const { hotelToDelete, deletedIds, handleRemove, handleCloseDialog, handleConfirmRemove, handleRemoveWithoutConfirm } = useRemoveHotel();
  const { bulkRemove } = useBulkRemoveHotels(handleRemoveWithoutConfirm);
  const { isManageMode, selectedIds, pendingOrder, enterManageMode, exitManageMode, toggleSelection, selectAll, clearSelection, setPendingOrder, reconcileAfterDelete } =
    useManageMode();
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [updateTravelMapComponent] = useUpdateTravelMapComponentMutation();
  const { updateWebsitePreview } = useUpdateWebsitePreview();
  const confirmedDeleteCountRef = useRef(0);
  const prevDeletedIdsRef = useRef(deletedIds);
  const history = useHistory();
  const router = useRouterHelper();
  const { tq } = useTranslation('accommodationsAdmin');
  const { toast } = useToast();

  const [isSuggestDialogOpen, setIsSuggestDialogOpen] = useState(false);

  const suggestShownKey = `${HOTEL_SUGGEST_SHOWN_PREFIX}:${eventHandle ?? ''}`;
  const hasAutoOpenedSuggestRef = useRef(withWindow(w => w.localStorage.getItem(suggestShownKey) === 'true', false));
  const pendingDeleteHotelRef = useRef<AccommodationCard | null>(null);

  const hasVenue = venueData?.hasVenue;
  const { value: hotelListActionBarVariant } = useRemoteFeatureFlag('hotelListActionBarExperiment');
  const isMoreMenuVariant = hotelListActionBarVariant === 'treatment';
  const { autoPopulatedAccommodations } = useAutoPopulatedAccommodations();
  const newlyPopulatedCount = autoPopulatedAccommodations.length;
  const tourTriggerRef = useRef<(() => void) | null>(null);
  const isSuggestingRef = useRef(false);
  const preExistingIdsRef = useRef<ReadonlySet<string> | null>(null);
  const [suggestPreExistingIds, setSuggestPreExistingIds] = useState<ReadonlySet<string> | null>(null);
  const [suggestRePopulationComplete, setSuggestRePopulationComplete] = useState(false);

  const { startAutoPopulation, status: autoPopulationStatus } = useAutoPopulation({
    mode: 'auto',
    context: 'HotelSuggestDialog',
    onComplete: () => {
      if (isSuggestingRef.current) {
        isSuggestingRef.current = false;
        setSuggestPreExistingIds(preExistingIdsRef.current);
        setSuggestRePopulationComplete(true);
      }
    },
  });

  const handleOpenSuggestDialog = useEventCallback((source: 'header' | 'emptyState') => {
    telemetry.hotelsSuggestClicked(pageSlug, { hotelCount: accommodationHotels.length, hasVenue: !!hasVenue, source });
    setIsSuggestDialogOpen(true);
  });

  const handleSuggestOption = useEventCallback((option: SuggestionOption) => {
    telemetry.hotelSuggestDialogOptionSelected(option.id);
    setIsSuggestDialogOpen(false);
    pendingDeleteHotelRef.current = null;

    if (option.id === 'suggestNeighborhoods') {
      const basePath = history.location.pathname.replace(/\/(stay-areas|guests)$/, '');
      history.push(`${basePath}/stay-areas`);
      return;
    }

    if (!hasVenue) return;

    preExistingIdsRef.current = new Set(accommodationHotels.map(h => h.id));
    isSuggestingRef.current = true;

    startAutoPopulation({
      venue: { latitude: venueData.latitude, longitude: venueData.longitude, googlePlaceId: venueData.googlePlaceId },
      scenario: AutopopulateAccommodationPlacesScenario.venueChange,
      userFeedback: USER_FEEDBACK_BY_OPTION[option.id as HotelSuggestionOptionId],
      idempotentId: `${uuidv4()}:userFeedback`,
    });
  });

  const handleDismissSuggestDialog = useEventCallback(() => {
    telemetry.hotelSuggestDialogOptionSelected('noThanks');
    setIsSuggestDialogOpen(false);

    const pendingHotel = pendingDeleteHotelRef.current;
    pendingDeleteHotelRef.current = null;
    if (pendingHotel) {
      handleRemove(pendingHotel);
    }
  });

  // Track confirmed deletions by observing deletedIds growth.
  // deletedIds shrinks on mutation failure (rollback), so this only counts
  // deletions that stick — avoiding false increments on failed mutations.
  useEffect(() => {
    if (deletedIds.size > prevDeletedIdsRef.current.size) {
      confirmedDeleteCountRef.current += deletedIds.size - prevDeletedIdsRef.current.size;
    }
    prevDeletedIdsRef.current = deletedIds;
  }, [deletedIds]);

  const handleRemoveWithSuggest = useEventCallback((hotel: AccommodationCard) => {
    const shouldInterceptWithSuggest =
      !hasAutoOpenedSuggestRef.current &&
      confirmedDeleteCountRef.current === 1 &&
      autoPopulationStatus !== 'PENDING' &&
      hasVenue &&
      hotel.type === TravelMapBlockType.travelMapAccommodationsHotel;

    if (shouldInterceptWithSuggest) {
      hasAutoOpenedSuggestRef.current = true;
      withWindow(w => w.localStorage.setItem(suggestShownKey, 'true'));
      pendingDeleteHotelRef.current = hotel;
      setIsSuggestDialogOpen(true);
      return;
    }
    handleRemove(hotel);
  });

  // Page note state
  const [updatePageContainer] = useUpdatePageContainerMutation();
  const canEditPageNote = Boolean(pageContainerId);

  const handlePageNoteSave = useEventCallback(async (pageSubTitle: string): Promise<boolean> => {
    if (!canEditPageNote) {
      toast(tq`Unable to save page note. Please try again.`);
      return false;
    }

    try {
      await updatePageContainer({
        variables: {
          payload: {
            id: pageContainerId,
            note: pageSubTitle,
          },
        },
      });
      sendMessageToPreview([
        {
          key: 'pageNote',
          value: {
            pageId: pageContainerId,
            pageSubTitle,
          },
        },
      ]);
      // "Get started" tour, step 4: the guest note is Where to Stay work as much as adding a hotel.
      // This is the page's OWN note save — distinct from the Neighborhoods note
      // (`useStayAreasNoteSave`) and from the inline editor reached via the preview iframe. All
      // three write a note on this page and all three now count.
      completeGetStartedTask('stay');
      return true;
    } catch {
      toast(tq`Unable to save page note. Please try again.`);
      return false;
    }
  });

  const { accommodationHotels: allAccommodationHotels, blockOrdering } = useSortedAccommodationsData({
    accommodations: accommodationsData,
    blockOrderingInitial: componentData.blockOrdering,
  });

  // Filter out optimistically deleted hotels
  const accommodationHotels = useMemo(() => allAccommodationHotels.filter(h => !deletedIds.has(h.id)), [allAccommodationHotels, deletedIds]);

  // Handle search params for add/edit flows
  const updateSearchParam = useUpdateSearchParam();
  const queryParamHelper = useQueryParamHelper();
  const addMatchedHotel = queryParamHelper.getValueString(EDIT_HOTEL_ID_KEY);
  const addStay = queryParamHelper.getValueString(ADD_STAY_SEARCH_PARAM_KEY);

  useEffect(() => {
    if (addMatchedHotel === 'true') {
      navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_HOTEL_ADD);
      updateSearchParam(EDIT_HOTEL_ID_KEY, null);
    }
  }, [addMatchedHotel, navigateToScreen, updateSearchParam, accommodationsData]);

  useEffect(() => {
    if (addStay === 'true') {
      navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_ADD);
      updateSearchParam(ADD_STAY_SEARCH_PARAM_KEY, null);
    }
  }, [addStay, navigateToScreen, updateSearchParam]);

  const handleAdd = useEventCallback(() => {
    telemetry.hotelsAddClicked(pageSlug, { hotelCount: accommodationHotels.length });
    navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_ADD);
  });

  const handleSuggest = useEventCallback(() => {
    if (getEmptyStateSuggestAction(!!hasVenue, autoPopulationStatus) === 'openSuggestDialog') {
      handleOpenSuggestDialog('emptyState');
      return;
    }
    telemetry.addAccommodationItemClicked('accommodations', {
      label: 'Add Hotel',
      itemType: TravelMapBlockType.travelMapAccommodationsHotel,
    });
    history.push(router.buildPath(`${PlanningRoutes.accommodations}/browse`));
  });

  const hasHotels = accommodationHotels.length > 0;

  const selectedHotels = useMemo(() => accommodationHotels.filter(h => selectedIds.has(h.id)), [accommodationHotels, selectedIds]);

  const handleEnterManageMode = useEventCallback(() => {
    telemetry.manageModeEnter(pageSlug, { hotelCount: accommodationHotels.length, isSuperAdmin: !!isSuperAdmin });
    enterManageMode();
  });

  const handleSelectAll = useEventCallback(() => {
    selectAll(accommodationHotels.map(h => h.id));
  });

  const handleOpenBulkDeleteDialog = useEventCallback(() => {
    telemetry.bulkDeleteDialog(pageSlug, { count: selectedIds.size, isSuperAdmin: !!isSuperAdmin });
    setBulkDeleteDialogOpen(true);
  });

  const handleBulkDeleteConfirm = useEventCallback(async () => {
    setBulkDeleteDialogOpen(false);
    telemetry.bulkDeleteSubmit(pageSlug, { count: selectedHotels.length, isSuperAdmin: !!isSuperAdmin });
    setIsBulkDeleting(true);
    try {
      await bulkRemove({
        hotels: selectedHotels,
        onSettled: ids => reconcileAfterDelete(ids),
      });
    } finally {
      setIsBulkDeleting(false);
    }
  });

  const handleSave = useEventCallback(async () => {
    if (!pendingOrder) {
      telemetry.manageModeSave(pageSlug, { reorderChanged: false, isSuperAdmin: !!isSuperAdmin });
      exitManageMode();
      return;
    }
    const favoriteItem = accommodationHotels.find(h => h.isFavorite);
    if (favoriteItem && pendingOrder[0]?.id !== favoriteItem.id) {
      toast(tq`Favorite hotel must remain first in the list`, { icon: <Error size={24} /> });
      return;
    }
    try {
      const response = await updateTravelMapComponent({
        variables: {
          payload: {
            id: componentData?.id,
            sortOrder: componentData?.sortOrder ?? 1,
            blockOrdering: pendingOrder.map(h => h.id),
          },
        },
      });
      if (response.errors) {
        addError(response.errors[0], { context: 'HotelsTab: manage mode save reorder failed' });
        toast(tq`Failed to update order`, { icon: <WarningTriangle /> });
        return;
      }
      // Explicit refetch — name-based `refetchQueries` silently skips queries mounted with skip:true.
      // Awaiting here also ensures accommodationHotels reflects the new order before exitManageMode
      // clears pendingOrder, preventing a flash to the pre-drag order.
      await refetchPageContainer();
      updateWebsitePreview();
      toast(tq`Order updated`, { icon: <CheckIcon /> });
      telemetry.manageModeSave(pageSlug, { reorderChanged: true, isSuperAdmin: !!isSuperAdmin });
      exitManageMode();
    } catch (error) {
      addError(error, { context: 'HotelsTab: manage mode save reorder failed' });
      toast(tq`Failed to update order`, { icon: <WarningTriangle /> });
    }
  });

  const handleCancelClick = useEventCallback(() => {
    if (pendingOrder) {
      setCancelConfirmOpen(true);
    } else {
      telemetry.manageModeCancel(pageSlug, { reorderPending: false, isSuperAdmin: !!isSuperAdmin });
      exitManageMode();
    }
  });

  const handleDiscardConfirm = useEventCallback(() => {
    setCancelConfirmOpen(false);
    telemetry.manageModeCancel(pageSlug, { reorderPending: true, isSuperAdmin: !!isSuperAdmin });
    exitManageMode();
  });

  const { loading: loadingEventInfo } = useEventInfo();

  // Stabilize the IDs array reference — only produce a new array when the set of IDs
  // actually changes, not on every refetch that returns the same hotels in a different
  // object identity. This prevents the pricing query from re-firing unnecessarily.
  const accommodationPlaceIds = useMemo(
    () => accommodationHotels.filter(accommodation => accommodation.type !== TravelMapBlockType.travelMapAccommodationsRoomblock).map(accommodation => accommodation.id),
    [accommodationHotels],
  );
  // Only re-fetch pricing when new IDs are added — removals don't need a re-fetch
  // because each tile reads its own cached price from Apollo independently.
  const prevIdsSetRef = useRef<Set<string>>(new Set(accommodationPlaceIds));
  const prevArrayRef = useRef<string[]>(accommodationPlaceIds);
  const stableAccommodationPlaceIds = useMemo(() => {
    const hasNewIds = accommodationPlaceIds.some(id => !prevIdsSetRef.current.has(id));
    prevIdsSetRef.current = new Set(accommodationPlaceIds);
    if (hasNewIds || prevArrayRef.current.length === 0) {
      prevArrayRef.current = accommodationPlaceIds;
      return accommodationPlaceIds;
    }
    // IDs only removed or unchanged — return previous array reference to skip pricing re-fetch
    return prevArrayRef.current;
  }, [accommodationPlaceIds]);

  // "More" dropdown items for the treatment variant of hotelListActionBarExperiment
  const moreMenuItems = useMemo(() => {
    const items: MoreMenuItem[] = [];
    if (hasVenue && autoPopulationStatus !== 'PENDING') {
      items.push({
        label: tq`Suggest hotels`,
        icon: AiSparkleIcon,
        onClick: () => handleOpenSuggestDialog('header'),
      });
    }
    if (isSuperAdmin && hasHotels) {
      items.push({
        label: tq`Edit hotels`,
        icon: Pencil,
        onClick: handleEnterManageMode,
      });
    }
    if (hasVenue && newlyPopulatedCount > 0) {
      items.push({
        label: tq`Why these picks`,
        icon: Info,
        onClick: () => tourTriggerRef.current?.(),
      });
    }
    return items;
  }, [hasVenue, autoPopulationStatus, isSuperAdmin, hasHotels, newlyPopulatedCount, handleOpenSuggestDialog, handleEnterManageMode, tq]);

  return (
    <Flex
      flexDirection="column"
      gap={6}
    >
      {/* Note to Guests — between venue header and Hotels header */}
      <FormControl
        typographyVariant="button2"
        label={<PageNoteLabel />}
        paddingX={{ _: 6, sm2: 8 }}
      >
        <PageNoteField
          note={pageNote}
          isDisabled={!canEditPageNote}
          onSave={handlePageNoteSave}
          onEditingEnd={refetchPageContainer}
        />
      </FormControl>

      <TabSectionHeader title={isManageMode ? undefined : tq`Hotels`}>
        {isManageMode ? (
          <>
            {!isBulkDeleting && selectedIds.size < accommodationHotels.length && (
              <TabSectionHeader.Ghost
                onClick={handleSelectAll}
                paddingX={0}
              >{tq`Select All (${selectedIds.size}/${accommodationHotels.length})`}</TabSectionHeader.Ghost>
            )}
            {!isBulkDeleting && selectedIds.size > 0 && <TabSectionHeader.Ghost onClick={clearSelection}>{tq`Clear`}</TabSectionHeader.Ghost>}
            {!isBulkDeleting && selectedIds.size > 0 && (
              <TabSectionHeader.Action
                intent="destructive"
                onClick={handleOpenBulkDeleteDialog}
              >{tq`Delete`}</TabSectionHeader.Action>
            )}
            {!isBulkDeleting && <TabSectionHeader.Ghost onClick={handleCancelClick}>{tq`Cancel`}</TabSectionHeader.Ghost>}
            {!isBulkDeleting && pendingOrder !== null && <TabSectionHeader.Action onClick={handleSave}>{tq`Save`}</TabSectionHeader.Action>}
          </>
        ) : (
          <>
            {/* StartTourButton always renders for dialog/tooltip lifecycle; hideButton suppresses its icon in treatment */}
            <StartTourButton
              showTour={true}
              suggestRePopulationComplete={suggestRePopulationComplete}
              onSuggestTooltipDismissed={() => setSuggestRePopulationComplete(false)}
              hideButton={isMoreMenuVariant}
              triggerRef={tourTriggerRef}
            />
            {isMoreMenuVariant ? (
              <>
                <Suspense fallback={null}>
                  <LazyMoreMenu items={moreMenuItems} />
                </Suspense>
                {/* Tight wrapper so the "Get started" tour's step-4 ring hugs the Add button. */}
                <Box
                  ref={getStartedStayTargetRef}
                  display="inline-flex"
                >
                  <TabSectionHeader.Action onClick={handleAdd}>{tq`Add`}</TabSectionHeader.Action>
                </Box>
              </>
            ) : (
              <>
                {isSuperAdmin && (
                  <TabSectionHeader.Action
                    variant="outline"
                    onClick={handleEnterManageMode}
                    isDisabled={!hasHotels}
                    ariaLabel={tq`Manage`}
                    paddingX={3}
                    border="1px solid"
                    borderColor="mono3"
                  >
                    <Drag size={16} />
                  </TabSectionHeader.Action>
                )}
                {hasVenue && autoPopulationStatus !== 'PENDING' && (
                  <TabSectionHeader.AiSuggest
                    showIcon={false}
                    onClick={() => handleOpenSuggestDialog('header')}
                  >{tq`Suggest`}</TabSectionHeader.AiSuggest>
                )}
                {/* Tight wrapper so the "Get started" tour's step-4 ring hugs the Add button. */}
                <Box
                  ref={getStartedStayTargetRef}
                  display="inline-flex"
                >
                  <TabSectionHeader.Action onClick={handleAdd}>{tq`Add`}</TabSectionHeader.Action>
                </Box>
              </>
            )}
          </>
        )}
      </TabSectionHeader>

      <Flex
        flexDirection="column"
        gap={6}
        paddingX={{ _: 6, sm2: 8 }}
      >
        <AsyncAutoPopulationStatus statusOverride={autoPopulationStatus} />

        {!accommodationsData ? (
          <HotelTileSkeletonList />
        ) : hasHotels ? (
          <>
            <HotelsList
              accommodationHotels={accommodationHotels}
              blockOrdering={blockOrdering}
              onRemove={handleRemoveWithSuggest}
              suggestPreExistingIds={suggestPreExistingIds}
              isManageMode={isManageMode}
              selectedIds={selectedIds}
              pendingOrder={pendingOrder}
              onToggleSelection={toggleSelection}
              onPendingOrderChange={setPendingOrder}
            />
            {/* Isolate lazy chunk suspense so StartTourButton (dialog registration) always mounts with the tab */}
            <Suspense fallback={null}>
              <LazyAiRecommendationsFeedback />
            </Suspense>
          </>
        ) : (
          <HotelsEmptyState onSuggest={handleSuggest} />
        )}
      </Flex>

      {hasHotels && (
        <HotelRemoveDialog
          isOpen={hotelToDelete !== null}
          hotelType={hotelToDelete?.type}
          onClose={handleCloseDialog}
          onConfirm={handleConfirmRemove}
        />
      )}

      <BulkRemoveDialog
        isOpen={bulkDeleteDialogOpen}
        count={selectedIds.size}
        onClose={() => setBulkDeleteDialogOpen(false)}
        onConfirm={handleBulkDeleteConfirm}
      />

      <DiscardReorderDialog
        isOpen={cancelConfirmOpen}
        onKeepEditing={() => setCancelConfirmOpen(false)}
        onDiscard={handleDiscardConfirm}
      />

      {!loadingEventInfo && hasHotels && (
        <RequestAccommodationPlacePricing
          accommodationPlaceIds={stableAccommodationPlaceIds}
          eventCurrency={accommodationsData?.travelSettings?.currencyCode}
        />
      )}

      <HotelSuggestDialog
        isOpen={isSuggestDialogOpen}
        onClose={handleDismissSuggestDialog}
        onSelect={handleSuggestOption}
      />
    </Flex>
  );
};

export default HotelsTab;
